import classNames, { Argument } from "classnames/dedupe";
import React, { ElementType, FC, SVGAttributes } from "react";
import * as AllIconComponents from "../../icons";

export interface ISvgIconProps extends Partial<SVGAttributes<any>> {
  /**
   * One of 3 variants:
   * 1) Existing SVG React component will be wrapped with classes and event handlers
   * 2) A custom icon Id, used as comparator in list of known icons
   * 3) A xlinkHref attribute to render icon from SVG sprite
   * */
  icon?: ElementType | string;
  isSprite?: boolean;
  /**
   * Wrap svg in icon-container class
   */
  isIcon?: boolean;
  /**
   * Sets cursor to pointer
   */
  isButton?: boolean;
  classes?: {
    root?: Argument;
  };
  /** A callback function for when this component is clicked   */
  onClick?: (...args: any[]) => void;
}

const SvgIcon: FC<ISvgIconProps> = ({
  icon,
  isSprite = false,
  isIcon = true,
  isButton = false,
  classes,
  ...rest
}) => {
  if (!icon) return null;

  const rootClasses = classNames(
    { "icon-container": isIcon },
    { "cursor-pointer": isButton },
    classes?.root,
  );

  /** Is a sprite icon */
  if (isSprite) {
    return (
      <svg className={rootClasses} data-testid="svg-icon" {...rest}>
        <use data-testid="svg-raw-sprite" xlinkHref={icon as string} />
      </svg>
    );
  }

  /** Is a predefined name of known icon components */
  if (typeof icon === "string") {
    if (Object.prototype.hasOwnProperty.call(AllIconComponents, icon)) {
      const KnownIconComponent =
        AllIconComponents[icon as keyof typeof AllIconComponents];
      return (
        <KnownIconComponent
          data-testid="svg-icon"
          {...(classes?.root ? { className: classNames(classes?.root) } : {})}
          {...rest}
        />
      );
    }
    console.error("icon", String(icon), "not found");
    return null;
  }

  /** By default - render an icon component */
  const IconComponent = icon as React.ElementType;

  /** <svg><svg>...</svg></svg> for compatibility with earlier components */
  return (
    <svg className={rootClasses} data-testid="svg-icon" {...rest}>
      <IconComponent {...rest} />
    </svg>
  );
};

export default SvgIcon;
